/**
 * GoogleTasksBoard — Todo personale (Google Tasks) a SCHEDE, sotto il calendario in Home.
 *
 * Mostra gli stessi task Google dell'admin loggato (endpoint /admin/google-tasks),
 * ma a finestre/card. In Home compaiono solo i task DA FARE, i più urgenti per
 * primi, max 8; "Vedi tutti" apre una finestra a tutto schermo con ricerca,
 * sezioni Da fare/Completati e CRUD completo (crea/modifica/completa/elimina).
 * Riusa TaskModal di GoogleTasksPanel per crea/modifica.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Loader2, CheckCircle2, Circle,
  Calendar as CalIcon, RefreshCw, ListTodo, Search, Link2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { TaskModal } from "./GoogleTasksPanel";

const HOME_LIMIT = 8;

function fmtDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function sortKey(t) {
  return `${t.due || "9999-99-99"}|${(t.title || "").toLowerCase()}`;
}

function GTCard({ t, busy, multiAccount, onToggle, onEdit, onDelete }) {
  const done = t.status === "completed";
  const overdue = !done && t.due && t.due < todayIso();
  return (
    <div className="bg-white border border-ink/10 rounded-sm p-4 flex flex-col gap-2 group relative hover:shadow-md transition-shadow" data-testid={`gt-card-${t.id}`}>
      <div className="flex items-start gap-2">
        <button onClick={() => onToggle(t)} disabled={busy} className="mt-0.5 shrink-0 text-slate-300 hover:text-emerald-600 transition-colors disabled:opacity-40" title={done ? "Riapri" : "Segna come fatto"} data-testid={`gt-card-toggle-${t.id}`}>
          {busy ? <Loader2 size={18} className="animate-spin" /> : done ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} />}
        </button>
        <div className={`flex-1 min-w-0 font-medium text-ink leading-snug break-words ${done ? "line-through opacity-60" : ""}`}>{t.title || "(senza titolo)"}</div>
      </div>
      {t.notes && <div className="text-[12px] text-slate1 line-clamp-3 pl-7 whitespace-pre-wrap">{t.notes}</div>}
      <div className="flex items-center justify-between pl-7 mt-auto pt-1 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          {t.due && <span className={`inline-flex items-center gap-1 text-[11px] ${overdue ? "text-rose-600 font-semibold" : "text-slate1"}`} data-testid={`gt-card-due-${t.id}`}><CalIcon size={11} /> {fmtDate(t.due)}</span>}
          {multiAccount && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 truncate max-w-[110px]" title={t.owner_email}>{t.owner_email.split("@")[0]}</span>}
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={() => onEdit(t)} disabled={busy} className="p-1 text-slate1 hover:text-ink disabled:opacity-40" title="Modifica" data-testid={`gt-card-edit-${t.id}`}><Pencil size={13} /></button>
          <button onClick={() => onDelete(t)} disabled={busy} className="p-1 text-rose-600 hover:bg-rose-50 rounded-sm disabled:opacity-40" title="Elimina" data-testid={`gt-card-del-${t.id}`}><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
}

function GTRow({ t, busy, multiAccount, onToggle, onEdit, onDelete }) {
  const done = t.status === "completed";
  const overdue = !done && t.due && t.due < todayIso();
  return (
    <div className="bg-white border border-ink/10 rounded-sm px-3 py-2.5 flex items-center gap-3 group" data-testid={`gt-row-${t.id}`}>
      <button onClick={() => onToggle(t)} disabled={busy} className="shrink-0 text-slate-300 hover:text-emerald-600 transition-colors disabled:opacity-40" title={done ? "Riapri" : "Segna come fatto"} data-testid={`gt-row-toggle-${t.id}`}>
        {busy ? <Loader2 size={18} className="animate-spin" /> : done ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`font-medium text-ink text-sm truncate ${done ? "line-through opacity-60" : ""}`}>{t.title || "(senza titolo)"}</div>
        {t.notes && <div className="text-[12px] text-slate1 truncate">{t.notes}</div>}
      </div>
      {t.due && <span className={`inline-flex items-center gap-1 text-[11px] shrink-0 ${overdue ? "text-rose-600 font-semibold" : "text-slate1"}`}><CalIcon size={11} /> {fmtDate(t.due)}</span>}
      {multiAccount && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 truncate max-w-[110px] shrink-0" title={t.owner_email}>{t.owner_email.split("@")[0]}</span>}
      <div className="flex gap-1 shrink-0">
        <button onClick={() => onEdit(t)} disabled={busy} className="p-1.5 text-slate1 hover:text-ink disabled:opacity-40" title="Modifica" data-testid={`gt-row-edit-${t.id}`}><Pencil size={14} /></button>
        <button onClick={() => onDelete(t)} disabled={busy} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm disabled:opacity-40" title="Elimina" data-testid={`gt-row-del-${t.id}`}><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

function AllTasksModal({ tasks, multiAccount, busyId, onClose, onToggle, onEdit, onDelete, onAdd, q, setQ }) {
  const term = q.trim().toLowerCase();
  const filtered = term ? tasks.filter((t) => `${t.title || ""} ${t.notes || ""}`.toLowerCase().includes(term)) : tasks;
  const todo = filtered.filter((t) => t.status !== "completed").sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  const done = filtered.filter((t) => t.status === "completed");
  return (
    <div className="fixed inset-0 z-[150] bg-ink/60 backdrop-blur-sm flex flex-col p-4 sm:p-8" data-testid="gt-all-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-cream w-full max-w-4xl mx-auto flex-1 min-h-0 flex flex-col border border-ink/15 shadow-2xl rounded-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">Todo personale · Google Tasks</div>
            <div className="font-display text-xl text-ink">{todo.length} da fare · {done.length} completati</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onAdd} className="inline-flex items-center gap-1.5 bg-ink text-cream px-3 py-2 text-[10px] tracking-[0.18em] uppercase rounded-sm" data-testid="gt-all-add"><Plus size={13} /> Task</button>
            <button onClick={onClose} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" data-testid="gt-all-close"><X size={18} /></button>
          </div>
        </div>
        <div className="px-6 py-3 border-b border-ink/10 shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca task..." className="w-full border border-ink/15 pl-9 pr-3 py-2 rounded-sm bg-white text-sm" data-testid="gt-all-search" />
          </div>
        </div>
        <div className="overflow-y-auto p-4 space-y-4">
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase text-slate1 mb-2">Da fare ({todo.length})</div>
            {todo.length === 0 ? <div className="text-sm text-slate-400 py-4 text-center">Niente da fare</div> : (
              <div className="space-y-2">{todo.map((t) => <GTRow key={`${t.owner_email}-${t.id}`} t={t} busy={busyId === t.id} multiAccount={multiAccount} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} />)}</div>
            )}
          </div>
          {done.length > 0 && (
            <div>
              <div className="text-[11px] tracking-[0.16em] uppercase text-slate1 mb-2">Completati ({done.length})</div>
              <div className="space-y-2">{done.map((t) => <GTRow key={`${t.owner_email}-${t.id}`} t={t} busy={busyId === t.id} multiAccount={multiAccount} onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} />)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GoogleTasksBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [modalTask, setModalTask] = useState(null); // null | "new" | task
  const [q, setQ] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/google-tasks");
      setData(r.data);
    } catch (err) {
      if (!silent) toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Impossibile caricare Google Tasks");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(() => load(true), 60000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const accounts = data?.accounts || [];
  const okAccounts = accounts.filter((a) => !a.needs_reconnect && !a.error && !a.api_disabled);
  const apiDisabledAccounts = accounts.filter((a) => a.api_disabled);
  const tasks = data?.tasks || [];
  const multiAccount = okAccounts.length > 1;

  const handleApiError = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail === "GOOGLE_TASKS_SCOPE_MISSING") toast.error("Permesso Google Tasks mancante: ricollega l'account Google");
    else if (detail === "GOOGLE_TASKS_API_DISABLED") toast.error("API Google Tasks non abilitata nel progetto Google Cloud");
    else toast.error(formatApiErrorDetail(detail) || "Errore Google Tasks");
  };
  const toggle = async (t) => {
    setBusyId(t.id);
    const newStatus = t.status === "completed" ? "needsAction" : "completed";
    try {
      await api.patch(`/admin/google-tasks/${t.id}`, { google_email: t.owner_email, list_id: t.list_id, status: newStatus });
      await load(true);
    } catch (err) { handleApiError(err); } finally { setBusyId(null); }
  };
  const remove = async (t) => {
    if (!window.confirm("Eliminare questo task da Google Tasks?")) return;
    setBusyId(t.id);
    try {
      await api.delete(`/admin/google-tasks/${t.id}`, { params: { google_email: t.owner_email, list_id: t.list_id } });
      await load(true);
      toast.success("Task eliminato");
    } catch (err) { handleApiError(err); } finally { setBusyId(null); }
  };

  const header = (
    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
      <div className="inline-flex items-center gap-2">
        <span className="text-[11px] tracking-[0.2em] uppercase text-slate1">Todo personale · Google Tasks</span>
        <button onClick={() => load()} disabled={loading} className="p-1 text-slate-400 hover:text-ink disabled:opacity-40" title="Aggiorna" data-testid="gt-board-refresh"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /></button>
      </div>
      <div className="flex items-center gap-2">
        {tasks.length > 0 && (
          <button onClick={() => setShowAll(true)} className="text-[11px] tracking-[0.14em] uppercase text-slate1 hover:text-ink" data-testid="gt-board-see-all">
            Vedi tutti ({tasks.length})
          </button>
        )}
        {okAccounts.length > 0 && (
          <button onClick={() => setModalTask("new")} className="inline-flex items-center gap-1.5 bg-ink text-cream px-3 py-2 text-[10px] tracking-[0.18em] uppercase rounded-sm" data-testid="gt-board-add">
            <Plus size={13} /> Task
          </button>
        )}
      </div>
    </div>
  );

  if (loading && !data) {
    return <div data-testid="gt-board"><div className="flex justify-center py-8 text-slate-400"><Loader2 size={20} className="animate-spin" /></div></div>;
  }

  const todo = tasks.filter((t) => t.status !== "completed").sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  const homeCards = todo.slice(0, HOME_LIMIT);

  return (
    <div data-testid="gt-board">
      {header}

      {apiDisabledAccounts.length > 0 && (
        <div className="mb-3 bg-rose-50 border border-rose-300 rounded-sm p-3 text-[12px] text-rose-900" data-testid="gt-board-api-disabled">
          <strong>API Google Tasks non abilitata</strong> nel progetto Google Cloud. Abilitala su{" "}
          <a href="https://console.cloud.google.com/apis/library/tasks.googleapis.com" target="_blank" rel="noreferrer" className="underline font-semibold">console.cloud.google.com → Tasks API</a>, poi ricarica.
        </div>
      )}

      {!data?.connected ? (
        <div className="w-full border border-dashed border-ink/20 rounded-sm p-8 text-center" data-testid="gt-board-not-connected">
          <ListTodo size={26} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
          <p className="text-sm text-slate1">Nessun account Google collegato</p>
          <Link to="/admin?tab=dropbox-docs" className="mt-3 inline-flex items-center gap-1.5 text-xs text-gold hover:text-amber-700 font-semibold uppercase tracking-wider" data-testid="gt-board-connect"><Link2 size={13} /> Collega da Collegamenti</Link>
        </div>
      ) : todo.length === 0 ? (
        <button onClick={() => (okAccounts.length > 0 ? setModalTask("new") : null)} className="w-full border border-dashed border-ink/20 rounded-sm p-8 text-center text-sm text-slate-400 hover:bg-white hover:border-ink/30 transition-colors" data-testid="gt-board-empty">
          {tasks.length > 0 ? "Nessun task da fare — i completati sono in «Vedi tutti»" : "Nessun task su Google Tasks — clicca per aggiungerne uno"}
        </button>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gt-board-grid">
            {homeCards.map((t) => (
              <GTCard key={`${t.owner_email}-${t.id}`} t={t} busy={busyId === t.id} multiAccount={multiAccount} onToggle={toggle} onEdit={(x) => setModalTask(x)} onDelete={remove} />
            ))}
          </div>
          {todo.length > HOME_LIMIT && (
            <button onClick={() => setShowAll(true)} className="mt-3 text-[11px] tracking-[0.12em] uppercase text-slate1 hover:text-ink" data-testid="gt-board-more">
              +{todo.length - HOME_LIMIT} altri da fare · apri elenco completo →
            </button>
          )}
        </>
      )}

      {showAll && (
        <AllTasksModal
          tasks={tasks}
          multiAccount={multiAccount}
          busyId={busyId}
          onClose={() => setShowAll(false)}
          onToggle={toggle}
          onEdit={(t) => setModalTask(t)}
          onDelete={remove}
          onAdd={() => setModalTask("new")}
          q={q}
          setQ={setQ}
        />
      )}

      {modalTask && (
        <TaskModal
          task={modalTask === "new" ? null : modalTask}
          accounts={okAccounts}
          onClose={() => setModalTask(null)}
          onSaved={async () => { setModalTask(null); await load(true); }}
        />
      )}
    </div>
  );
}
